import { Box, Page, Skeleton, Stack } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface Props {
  mode: "full" | "compact";
}

export const EmployeePageHeaderSkeleton = ({ mode }: Props) => {
  const { t } = useTranslation();

  const variants = {
    full: {
      base: "h-57 transition-all",
    },
    compact: {
      base: "h-24 transition-all",
    },
  };
  const showOnFull = mode == "full" ? "opacity-100" : "opacity-0";
  const showOnCompact = mode == "compact" ? "opacity-100" : "opacity-0";
  const themeVariant = variants[mode];

  return (
    <Box className={"flex bg-light h-screen overflow-hidden"}>
      <Stack className={"w-76 overflow-y-auto"}>
        <div className={"sticky top-0 z-10 bg-light"}>
          <Page.Header title={t("core:profile")}>
            <Stack direction={"horizontal"} gap={"sm"}>
              <Skeleton width={36} height={28} className={"rounded-xs"} />
            </Stack>
          </Page.Header>
        </div>

        <div className={"px-6"}>
          <Stack direction={"vertical"} gap={"md"} items={"center"}>
            <Box className={"relative bg-light rounded-lg"}>
              <Skeleton
                variant={"rectangular"}
                className={"w-41 h-41 border-3 border-light rounded-lg"}
              />
            </Box>

            <Stack items={"center"} gap={"md"} className={"pb-(--gap-lg) w-full"}>
              <Stack items={"center"} justify={"center"} className={"w-full"}>
                <Skeleton className={"w-44 h-7"} />
                <Skeleton className={"w-34 h-5 mt-2"} />
                <Skeleton className={"w-28 h-4 mt-2"} />
              </Stack>

              <Skeleton className={"w-36 h-7 rounded-full"} />
              <Skeleton className={"w-30 h-10 rounded-xs"} />
            </Stack>
          </Stack>

          <Stack gap={"2xl"} className={"pt-2 pb-8"}>
            <Stack gap={"md"}>
              <Skeleton className={"w-20 h-4"} />
              <Stack gap={"sm"}>
                <Skeleton className={"w-full h-5"} />
                <Skeleton className={"w-5/6 h-5"} />
                <Skeleton className={"w-4/5 h-5"} />
              </Stack>
            </Stack>

            <Stack gap={"md"}>
              <Skeleton className={"w-24 h-4"} />
              <Stack gap={"sm"}>
                <Skeleton className={"w-full h-5"} />
                <Skeleton className={"w-3/4 h-5"} />
              </Stack>
            </Stack>
          </Stack>
        </div>
      </Stack>

      <div className={"flex-1 overflow-y-auto shadow-left"}>
        <div className={twMerge(themeVariant.base, "relative")}>
          <Skeleton className={"w-full h-full absolute inset-0"} variant={"rectangular"} />

          <div
            style={{
              background:
                "linear-gradient(0deg, var(--color-light) 0%, color-mix(in srgb, var(--color-light) 0%, transparent) 100%)",
              boxShadow: "0px -1px 0px 0px color-mix(in srgb, var(--color-light) 70%, transparent)",
            }}
            className={twMerge("w-full absolute inset-0 backdrop-blur-sm z-0", showOnCompact)}
          />
          <div className={"absolute bottom-0 left-0 right-0"}>
            <div
              style={{
                background:
                  "linear-gradient(0deg, var(--color-light) 0%, color-mix(in srgb, var(--color-light) 0%, transparent) 100%)",
                boxShadow:
                  "0px -1px 0px 0px color-mix(in srgb, var(--color-light) 70%, transparent)",
              }}
              className={twMerge("w-full absolute inset-0 backdrop-blur-sm z-0", showOnFull)}
            />

            <div
              aria-hidden
              className={"relative z-10 grid grid-cols-6 w-full border-b border-black/10"}
            >
              <SkeletonTab className={"w-20"} />
              <SkeletonTab className={"w-24"} />
              <SkeletonTab className={"w-16"} />
              <SkeletonTab className={"w-32"} />
              <SkeletonTab className={"w-24"} />
              <SkeletonTab className={"w-16"} />
            </div>
          </div>
        </div>

        <div className={"px-6 pb-16"}>
          <div className={"container-md mx-auto"}>
            <Stack className={"pt-16 gap-16"}>
              <SkeletonSection />
              <SkeletonSection compact />
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

function SkeletonTab({ className }: { className: string }) {
  return (
    <div className={"py-4 px-5 min-w-0"}>
      <Skeleton className={twMerge("h-4 mx-auto", className)} />
    </div>
  );
}

function SkeletonSection({ compact = false }: { compact?: boolean }) {
  const itemCount = compact ? 2 : 4;

  return (
    <Stack gap={"md"}>
      <Stack gap={"xs"}>
        <Skeleton className={"w-36 h-6"} />
        <Skeleton className={"w-24 h-4"} />
      </Stack>

      <Box className={"flex flex-row gap-(--gap-md) flex-wrap"}>
        {Array.from({ length: itemCount }).map((_, index) => (
          <Stack key={`employee-page-skeleton-card-${index}`} className={"w-41"} gap={"sm"}>
            <Skeleton className={"w-41 h-41 rounded-lg"} />
            <Skeleton className={"w-40 h-4"} />
            <Skeleton className={"w-32 h-4"} />
          </Stack>
        ))}
      </Box>
    </Stack>
  );
}
